import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Alert, Spinner, Badge } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { doc, setDoc, getDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

const EVENT_ID = 'faith_session_2026_01_21';

const sessions = [
  {
    id: 'morning',
    name: "Faith's Session (Morning)",
    time: 'Wednesday, 21 Jan 2026, 8:00 AM - 10:30 AM',
  },
  {
    id: 'evening',
    name: "Faith's Session (Evening)",
    time: 'Wednesday, 21 Jan 2026, 5:00 PM - 7:30 PM',
  },
];

const allowedCohortNames = [
  'Takealot Personal Shopper - NPI Cohort 1',
  'Takealot Personal Shopper - NPI Cohort 2',
  'Takealot Personal Shopper - NPI Cohort 3',
  'Takealot Personal Shopper - NPI Cohort 4'
];

const SessionRSVP = () => {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userRsvp, setUserRsvp] = useState(null);
  const [isEligible, setIsEligible] = useState(false);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);

  useEffect(() => {
    if (currentUser && isEligible) {
      checkUserRsvp();
    } else if (currentUser && eligibilityChecked) {
      setLoading(false);
    }
  }, [currentUser, isEligible, eligibilityChecked]);

  useEffect(() => {
    const checkEligibility = async () => {
      if (!currentUser || userProfile?.role !== 'learner') {
        setIsEligible(false);
        setEligibilityChecked(true);
        return;
      }

      if (userProfile?.cohortName) {
        setIsEligible(allowedCohortNames.includes(userProfile.cohortName));
        setEligibilityChecked(true);
        return;
      }

      if (!userProfile?.cohortId) {
        setIsEligible(false);
        setEligibilityChecked(true);
        return;
      }

      try {
        const cohortDoc = await getDoc(doc(db, 'cohorts', userProfile.cohortId));
        if (cohortDoc.exists()) {
          const cohortData = cohortDoc.data();
          const cohortName = cohortData.name || cohortData.cohortName || '';
          setIsEligible(allowedCohortNames.includes(cohortName));
        } else {
          setIsEligible(false);
        }
      } catch (err) {
        devError('Error checking RSVP eligibility:', err);
        setIsEligible(false);
      } finally {
        setEligibilityChecked(true);
      }
    };

    checkEligibility();
  }, [currentUser, userProfile?.role, userProfile?.cohortId, userProfile?.cohortName]);

  const checkUserRsvp = async () => {
    try {
      setLoading(true);
      const rsvpDocRef = doc(db, 'events', EVENT_ID, 'rsvps', currentUser.uid);
      const rsvpDoc = await getDoc(rsvpDocRef);

      if (rsvpDoc.exists()) {
        setUserRsvp(rsvpDoc.data());
      } else {
        setUserRsvp(null);
      }
    } catch (err) {
      devError('Error checking RSVP status:', err);
      setError('Could not check your RSVP status. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleRsvp = async (sessionId) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const selectedSession = sessions.find(s => s.id === sessionId);
      if (!selectedSession) {
        throw new Error('Invalid session selected.');
      }

      const rsvpDocRef = doc(db, 'events', EVENT_ID, 'rsvps', currentUser.uid);
      
      const rsvpData = {
        eventId: EVENT_ID,
        sessionId: selectedSession.id,
        sessionName: selectedSession.name,
        sessionTime: selectedSession.time,
        studentId: currentUser.uid,
        studentName: userProfile?.displayName || currentUser.email,
        studentEmail: userProfile?.email,
        rsvpedAt: serverTimestamp(),
      };

      await setDoc(rsvpDocRef, rsvpData);

      setSuccess(`You have successfully RSVP'd for the ${selectedSession.name}.`);
      await checkUserRsvp();

    } catch (err) {
      devError('Error submitting RSVP:', err);
      setError('Failed to submit your RSVP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRsvp = async () => {
    if (!window.confirm('Are you sure you want to cancel your RSVP?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const rsvpDocRef = doc(db, 'events', EVENT_ID, 'rsvps', currentUser.uid);
      await deleteDoc(rsvpDocRef);

      setSuccess('Your RSVP has been cancelled.');
      setUserRsvp(null);

    } catch (err) {
      devError('Error cancelling RSVP:', err);
      setError('Failed to cancel your RSVP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!eligibilityChecked || !isEligible) {
    return null;
  }

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="bg-secondary text-white">
        <h6 className="mb-0">RSVP for Faith's Sessions</h6>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading RSVP status...</p>
          </div>
        ) : userRsvp ? (
          <div>
            <Alert variant="info">
              <p className="mb-2">
                <strong>You have RSVP'd for:</strong>
              </p>
              <h5 className="mb-1">{userRsvp.sessionName}</h5>
              <p className="mb-0">{userRsvp.sessionTime}</p>
            </Alert>
            <Button variant="outline-danger" size="sm" onClick={handleCancelRsvp}>
              Cancel RSVP
            </Button>
          </div>
        ) : (
          <div>
            <p>Please select one of the following sessions to attend:</p>
            <Row>
              {sessions.map(session => (
                <Col md={6} key={session.id} className="mb-3">
                  <Card className="h-100">
                    <Card.Body className="d-flex flex-column justify-content-between">
                      <div>
                        <Card.Title>{session.name}</Card.Title>
                        <Card.Text>{session.time}</Card.Text>
                      </div>
                      <Button
                        variant="primary"
                        onClick={() => handleRsvp(session.id)}
                        disabled={loading}
                      >
                        RSVP for this session
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SessionRSVP;
